// ==============================|| TENANT TYPES ||============================== //

import { CaseClosingConfigurationDto } from './caseClosing';
import { NamedEntityMinDto } from './other';

export enum DayOfWeek {
    MONDAY = 'MONDAY',
    TUESDAY = 'TUESDAY',
    WEDNESDAY = 'WEDNESDAY',
    THURSDAY = 'THURSDAY',
    FRIDAY = 'FRIDAY',
    SATURDAY = 'SATURDAY',
    SUNDAY = 'SUNDAY',
}

export interface TenantDto {
    id: string;
    name: string;
    baseCurrencyTypeCode: string;
    /** The CURRENCY code the create forms start with while DEFAULT_CURRENCY is on for the colleague. */
    defaultCurrencyTypeCode: string | null;
    /** What the firm calls the field ADMINISTRATOR_FIELD shows; null when unnamed. */
    administratorFieldName: string | null;
    emailOrganizer?: string;
    partyNumberFormat?: string;
    // Relationship type codes whose parties get a party number, and whose number the contact page shows
    partyNumberGeneratedFor?: string[];
    partyNumberShownFor?: string[];
    // Whether party names carry the `[number]` prefix
    showPartyNumberWithName?: boolean;
    // Tenant-wide per-interval-type invoice autogeneration schedule, keyed by invoicing interval
    // type code (read-only here; edited on the Configuration page)
    invoiceAutoGenerationDaysOfMonth: Record<string, number>;
    invoiceAutoGenerationDaysOfWeek: Record<string, DayOfWeek>;
    // Whether an uploaded logo replaces the default one, readable then at /v1/tenants/current/logo
    hasCustomLogo: boolean;
    // Whether the tenant has uploaded a square icon, readable then at /v1/tenants/current/icon
    hasCustomIcon: boolean;
}

export interface TenantConfigurationDto {
    debtCollectionReminderIntervals: ReminderIntervalDto[];
    isTimecardEntryPreviousMonthBlocked: boolean;
    timecardEntryDeadlineWithinCurrentMonth: number;
    isTimecardEntryPreviousWeekBlocked: boolean;
    timecardEntryDeadlineWithinCurrentWeek: DayOfWeek;
    invoiceAccountingDayOfMonth: number;
    primaryOfficeForCasesId: number | null;
    matterBlockingDebtClientThresholdAmount?: number;
    matterBlockingGracePeriodDays: number;
    matterBlockingWarningPeriodDays: number;
    conversionRatePrecision: number;
    invoicingPaymentCurrencyPrecision: number;
    invoicingBaseCurrencyPrecision: number;
    invoicingInvoiceCurrencyPrecision: number;
    invoiceNonBillableTcBgColor?: string;
    hideInvoicesOlderThanDays?: number | null;
    isDraftInvoiceNotifiedToSupervisingPartner: boolean;
    timecardIncrementValues: number[] | null;
    defaultTimecardIncrement: number | null;
    // The employees a new case starts out Handled by; resolved from the stored ids, so one since
    // deleted or deactivated is simply absent.
    defaultHandledByEmployees: NamedEntityMinDto[];
    // On: the defaults go onto confidential cases only, and the add form offers them once the case is ticked
    // confidential rather than on every new case.
    isDefaultHandledByOnlyWhenConfidential: boolean;
    isInvoiceAutoGenerationEnabled: boolean;
    // Per-interval-type autogeneration schedule, keyed by interval type code; complete
    // (defaults filled) for every schedulable interval type
    invoiceAutoGenerationDaysOfMonth: Record<string, number>;
    invoiceAutoGenerationDaysOfWeek: Record<string, DayOfWeek>;
    invoiceLogo?: InvoiceLogoConfigurationDto;
    progressTrackingHoursMetric: ProgressTrackingHoursMetric;
    caseClosing: CaseClosingConfigurationDto;
    /** The fund accounts client asset records are booked on, in their configured order. */
    clientAssetAccounts: ClientAssetAccount[];
    /** Days a draft nobody saves is kept, 1 to 3650. */
    draftRetentionDays: number;
    /** Days after a request a BD effort warns before the same person is asked again, 0 to 3650; 0 never warns. */
    rankingsRefereeRestPeriodDays: number;
    /** The admin fee, in percent, the contract form fills in when a service agreement gets ADMIN_FEE_PERMITTED. */
    defaultAdminFeePercentage: number | null;
    defaultCurrencyTypeCode: string | null;
    administratorFieldName: string | null;
    hasCustomIcon: boolean;
}

/** A client asset fund account; records store its name, so a name in use cannot change. */
export interface ClientAssetAccount {
    name: string;
    active: boolean;
}

/** Which of an employee's hours the dashboard progress tracking counts against their targets. */
export type ProgressTrackingHoursMetric = 'CLIENT_HOURS';

/** Readable by every signed-in user, unlike the rest of the tenant configuration. */
export interface ProgressTrackingConfigurationDto {
    hoursMetric: ProgressTrackingHoursMetric;
}

export interface InvoiceLogoConfigurationDto {
    hasLogoTemplates: boolean;
    hasCustomLogo: boolean;
    recommendedWidth: number;
    recommendedHeight: number;
}

interface ReminderIntervalDto {
    debtReminderType: string;
    days: number;
    nextDebtReminderType?: string;
    isPenaltyCalculationTriggered?: boolean;
}

export interface DebtCollectionUpdateDto {
    debtCollectionReminderIntervals: ReminderIntervalDto[];
    matterBlockingDebtClientThresholdAmount: number | null;
    matterBlockingGracePeriodDays: number | null;
    matterBlockingWarningPeriodDays: number | null;
}

export interface ReminderIntervalsForm {
    debtCollectionReminderIntervals: ReminderIntervalDto[];
}

export interface AutoblockCriteriaForm {
    matterBlockingDebtClientThresholdAmount: number | null;
    matterBlockingGracePeriodDays: number | null;
    matterBlockingWarningPeriodDays: number | null;
}

export interface TimecardEntryControlUpdateDto {
    isTimecardEntryPreviousMonthBlocked: boolean;
    timecardEntryDeadlineWithinCurrentMonth: number | null;
    isTimecardEntryPreviousWeekBlocked: boolean;
    timecardEntryDeadlineWithinCurrentWeek: DayOfWeek | null;
}

export interface TimecardIncrementUpdateDto {
    timecardIncrementValues: number[];
    defaultTimecardIncrement: number;
}

export interface ImportationDateUpdateDto {
    invoiceAccountingDayOfMonth: number | null;
}

export interface PrimaryOfficeForCasesUpdateDto {
    primaryOfficeForCasesId: number | null;
}

export interface DefaultHandledByEmployeesUpdateDto {
    defaultHandledByEmployeeIds: number[];
    // Left out keeps the stored setting.
    isDefaultHandledByOnlyWhenConfidential?: boolean;
}

export interface ConversionRateConfigurationUpdateDto {
    conversionRatePrecision: number | null;
    invoicingPaymentCurrencyPrecision: number | null;
    invoicingBaseCurrencyPrecision: number | null;
    invoicingInvoiceCurrencyPrecision: number | null;
}

export interface HideInvoicesOlderThanDaysUpdateDto {
    hideInvoicesOlderThanDays: number | null;
}

export interface DraftInvoiceSupervisingPartnerNotificationUpdateDto {
    isDraftInvoiceNotifiedToSupervisingPartner: boolean;
}

export interface DraftRetentionDaysUpdateDto {
    draftRetentionDays: number;
}

export interface RankingsRefereeRestPeriodUpdateDto {
    rankingsRefereeRestPeriodDays: number;
}

export interface DefaultAdminFeePercentageUpdateDto {
    defaultAdminFeePercentage: number | null;
}

export interface DefaultCurrencyUpdateDto {
    defaultCurrencyTypeCode: string | null;
}

export interface AdministratorFieldNameUpdateDto {
    administratorFieldName: string | null;
}

/** Replaces the whole account list. */
export interface ClientAssetAccountsUpdateDto {
    clientAssetAccounts: ClientAssetAccount[];
}

export interface InvoiceAutoGenerationUpdateDto {
    isInvoiceAutoGenerationEnabled: boolean;
    invoiceAutoGenerationDaysOfMonth: Record<string, number> | null;
    invoiceAutoGenerationDaysOfWeek: Record<string, DayOfWeek> | null;
}

export enum DebtReminderType {
    REMINDER_GRACE_PERIOD = 'REMINDER_GRACE_PERIOD',
    TYPE1 = 'TYPE1',
    TYPE2 = 'TYPE2',
    TYPE3 = 'TYPE3',
    TYPE4 = 'TYPE4',
    OVERVIEW = 'OVERVIEW',
}
